import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Bell, Plus, Trash2, Eye, Star, Loader2, TrendingUp, TrendingDown, Target } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ThemeToggle } from "@/components/ThemeToggle";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface WatchlistItem {
  id: string;
  ticker: string;
  targetPrice: number | null;
  alertType: "above" | "below" | "pct_change";
  pctThreshold: number | null;
  notes: string;
  addedAt: string;
}

export default function PriceAlertsWatchlist() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [newTicker, setNewTicker] = useState("");
  const [newTarget, setNewTarget] = useState("");
  const [newAlertType, setNewAlertType] = useState<"above" | "below" | "pct_change">("above");
  const [newPctThreshold, setNewPctThreshold] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setUser(session?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setUser(session?.user ?? null));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    loadWatchlist();
  }, [user]);

  const loadWatchlist = async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("watchlist_alerts")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (data) {
      setWatchlist(data.map((d: any) => ({
        id: d.id,
        ticker: d.ticker,
        targetPrice: d.target_price,
        alertType: d.alert_type,
        pctThreshold: d.pct_threshold,
        notes: d.notes || "",
        addedAt: d.created_at,
      })));
    }
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!newTicker.trim()) {
      toast({ title: "Ticker required", variant: "destructive" });
      return;
    }
    if (!user) return;

    const { error } = await (supabase as any).from("watchlist_alerts").insert({
      user_id: user.id,
      ticker: newTicker.trim().toUpperCase(),
      target_price: newTarget ? parseFloat(newTarget) : null,
      alert_type: newAlertType,
      pct_threshold: newPctThreshold ? parseFloat(newPctThreshold) : null,
      notes: newNotes.trim() || null,
    });

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }

    setNewTicker("");
    setNewTarget("");
    setNewPctThreshold("");
    setNewNotes("");
    await loadWatchlist();
    toast({ title: "Added", description: `${newTicker.toUpperCase()} added to watchlist.` });
  };

  const handleDelete = async (id: string) => {
    await (supabase as any).from("watchlist_alerts").delete().eq("id", id);
    setWatchlist(prev => prev.filter(w => w.id !== id));
    toast({ title: "Removed from watchlist" });
  };

  const alertTypeLabel = (type: string) => {
    switch (type) {
      case "above": return "Price Above";
      case "below": return "Price Below";
      case "pct_change": return "% Change";
      default: return type;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-rose-500/10 rounded-lg">
                <Bell className="h-5 w-5 text-rose-500" />
              </div>
              <h1 className="text-xl font-bold text-foreground">Price Alerts & Watchlist</h1>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
        {/* Add to Watchlist */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Plus className="h-4 w-4 text-primary" /> Add to Watchlist
            </CardTitle>
            <CardDescription>Add a stock ticker with optional price target or percentage alert.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
              <Input
                placeholder="Ticker (e.g. AAPL)"
                value={newTicker}
                onChange={(e) => setNewTicker(e.target.value)}
                className="sm:col-span-1"
              />
              <Select value={newAlertType} onValueChange={(v: any) => setNewAlertType(v)}>
                <SelectTrigger className="sm:col-span-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="above">Price Above</SelectItem>
                  <SelectItem value="below">Price Below</SelectItem>
                  <SelectItem value="pct_change">% Change</SelectItem>
                </SelectContent>
              </Select>
              {newAlertType === "pct_change" ? (
                <Input
                  placeholder="% threshold"
                  type="number"
                  value={newPctThreshold}
                  onChange={(e) => setNewPctThreshold(e.target.value)}
                  className="sm:col-span-1"
                />
              ) : (
                <Input
                  placeholder="Target price"
                  type="number"
                  value={newTarget}
                  onChange={(e) => setNewTarget(e.target.value)}
                  className="sm:col-span-1"
                />
              )}
              <Input
                placeholder="Notes (optional)"
                value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)}
                className="sm:col-span-2"
              />
              <Button onClick={handleAdd} className="sm:col-span-1">
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Watchlist Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">Watching</p>
              <p className="text-2xl font-bold text-foreground">{watchlist.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">Price Targets</p>
              <p className="text-2xl font-bold text-rose-500">{watchlist.filter(w => w.targetPrice).length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">% Alerts</p>
              <p className="text-2xl font-bold text-amber-500">{watchlist.filter(w => w.alertType === "pct_change").length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Watchlist Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-500" /> Your Watchlist
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : watchlist.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No stocks in your watchlist yet. Add one above!</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ticker</TableHead>
                    <TableHead>Alert Type</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {watchlist.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-semibold">{item.ticker}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {item.alertType === "above" && <TrendingUp className="h-3 w-3 mr-1" />}
                          {item.alertType === "below" && <TrendingDown className="h-3 w-3 mr-1" />}
                          {item.alertType === "pct_change" && <Target className="h-3 w-3 mr-1" />}
                          {alertTypeLabel(item.alertType)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {item.alertType === "pct_change"
                          ? item.pctThreshold ? `${item.pctThreshold}%` : "—"
                          : item.targetPrice ? `$${item.targetPrice}` : "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{item.notes || "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(item.addedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
